import * as Yup from 'yup';
import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  InputAdornment,
  Link,
  Table,
  TableBody,
  TableContainer,
  TextField,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { fCurrencyRupees } from 'src/utils/format-number';
import { TABLE_HEAD_CELL_TYPE } from 'src/utils/common-types';

import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getPaginatedSuppliers } from 'src/redux/slices/suppliers';
import {
  createPackagingPurchaseOrder,
  TCreatePackagingPurchaseOrder,
  TCreatePackagingPurchaseOrderItem,
  updatePackagingPurchaseOrder,
} from 'src/redux/slices/packaging-purchase-order';

import Scrollbar from 'src/components/scrollbar';
import { TableHeadCustom } from 'src/components/table';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import CustomDatePicker from 'src/components/custom-datepicker/custom-datepicker';

import { ISupplierListItem } from 'src/types/supplier';
import { IPackagingPurchaseOrderDetailedItem } from 'src/types/packaging-purchase-order';

import { paths } from '../../routes/paths';
import PackagingPurchaseOrderNewItemRow from './packaging-purchase-order-new-item-row';
import PackagingPurchaseOrderItemsSummary, {
  IPackagingSummaryLineItem,
} from './packaging-purchase-order-items-summary';

const LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  { id: 'itemNo', label: '#', align: 'left', primary: true },
  { id: 'itemName', label: 'Item', align: 'left', primary: true, minWidth: 280 },
  { id: 'quantity', label: 'Quantity', align: 'left', primary: true },
];

export type ConditionalSchema<T> = T extends string
  ? Yup.StringSchema
  : T extends number
  ? Yup.NumberSchema
  : T extends boolean
  ? Yup.BooleanSchema
  : T extends Record<any, any>
  ? Yup.AnyObjectSchema
  : T extends Array<any>
  ? Yup.ArraySchema<any, any>
  : Yup.AnySchema;

export type Shape<Fields> = {
  [Key in keyof Fields]: ConditionalSchema<Fields[Key]>;
};

type Props = {
  currentPurchaseOrder?: IPackagingPurchaseOrderDetailedItem;
};

export default function PackagingPurchaseOrderNewEditForm({ currentPurchaseOrder }: Props) {
  const dispatch = useAppDispatcher();

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { suppliers } = useAppSelector((state) => state.suppliers);

  const [searchQuery, setSearchQuery] = useState(
    currentPurchaseOrder?.supplier ? currentPurchaseOrder.supplier.supplierName : ''
  );
  const [subTotal, setSubTotal] = useState<number>(0);

  const [itemDisplayCache, setItemDisplayCache] = useState<
    Record<string, { itemName: string; itemCode?: string }>
  >(() =>
    (currentPurchaseOrder?.items ?? []).reduce(
      (acc, item) => ({
        ...acc,
        [item.packagingItem._id]: {
          itemName: item.packagingItem.itemName,
          itemCode: item.packagingItem.itemCode,
        },
      }),
      {} as Record<string, { itemName: string; itemCode?: string }>
    )
  );

  const handleItemSelected = useCallback(
    (itemId: string, display: { itemName: string; itemCode?: string }) => {
      setItemDisplayCache((prev) => ({ ...prev, [itemId]: display }));
    },
    []
  );

  const NewPackagingPurchaseOrderSchema = Yup.object<
    Shape<TCreatePackagingPurchaseOrder>
  >().shape({
    supplier: Yup.string().required('Supplier is required'),
    purchaseDate: Yup.date().typeError('Invalid purchase date').required('Purchase date is required'),
    discount: Yup.number().typeError('').min(0, 'Discount cannot be negative').required(),
    isPercentageDiscount: Yup.boolean().required(),
    totalAfterDiscount: Yup.number().typeError('').min(0).required(),
    remarks: Yup.string().nullable(),
    items: Yup.array()
      .of(
        Yup.object<Shape<TCreatePackagingPurchaseOrderItem>>().shape({
          packagingItem: Yup.string().required('Packaging item is required'),
          quantity: Yup.number()
            .typeError('')
            .moreThan(0, 'Quantity must be greater than zero')
            .required('Quantity is required'),
          unitPrice: Yup.number().typeError('').min(0, 'Unit cost cannot be negative').nullable(),
          discount: Yup.number().typeError('').min(0, 'Discount cannot be negative').required(),
          totalAfterDiscount: Yup.number().typeError('').min(0).required(),
        })
      )
      .min(1, 'At least one item is required')
      .required('Items are required'),
  });

  const defaultItem = useMemo(
    () => ({
      packagingItem: '',
      quantity: 1,
      unitPrice: null,
      discount: 0,
      totalAfterDiscount: 0,
    }),
    []
  );

  const defaultValues = useMemo<TCreatePackagingPurchaseOrder>(
    () => ({
      supplier: currentPurchaseOrder?.supplier?._id ?? '',
      purchaseDate: currentPurchaseOrder?.purchaseDate
        ? new Date(currentPurchaseOrder.purchaseDate)
        : new Date(),
      discount: currentPurchaseOrder?.discount ?? 0,
      isPercentageDiscount: false,
      totalAfterDiscount: currentPurchaseOrder?.totalAfterDiscount ?? 0,
      remarks: currentPurchaseOrder?.remarks ?? null,
      items: currentPurchaseOrder?.items.map((item) => ({
        packagingItem: item.packagingItem._id,
        quantity: item.quantity,
        unitPrice: item.unitPrice ?? null,
        discount: item.discount ?? 0,
        totalAfterDiscount: item.totalAfterDiscount ?? 0,
      })) ?? [defaultItem],
    }),
    [currentPurchaseOrder, defaultItem]
  );

  const methods = useForm({
    resolver: yupResolver(NewPackagingPurchaseOrderSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    reset,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const watchItems = useWatch({ control, name: 'items' });
  const watchDiscount = useWatch({ control, name: 'discount' });

  const summaryItems: IPackagingSummaryLineItem[] = fields.map((field, index) => {
    const item = watchItems[index];
    const display = itemDisplayCache[item?.packagingItem ?? ''];

    return {
      key: field.id,
      itemName: display?.itemName ?? '',
      itemCode: display?.itemCode,
      quantity: item?.quantity ?? 0,
      unitPrice: item?.unitPrice ?? 0,
      discount: item?.discount ?? 0,
      totalAfterDiscount: item?.totalAfterDiscount ?? 0,
    };
  });

  const onSubmit = handleSubmit(async (data) => {
    const formData: TCreatePackagingPurchaseOrder = {
      ...data,
      remarks: data.remarks ?? null,
    };

    try {
      if (currentPurchaseOrder) {
        await dispatch(
          updatePackagingPurchaseOrder({
            purchaseOrderId: currentPurchaseOrder._id,
            poTransaction: formData,
          })
        ).unwrap();

        enqueueSnackbar('Purchase order updated');
      } else {
        await dispatch(
          createPackagingPurchaseOrder({
            poTransaction: formData,
          })
        ).unwrap();

        enqueueSnackbar('Purchase order created');
      }

      navigate(paths.dashboard.packagingPurchaseOrder.root);
      reset();
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(e?.message, {
          variant: 'error',
        });
      }
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPaginatedSuppliers = useCallback(
    debounce((query) => {
      dispatch(
        getPaginatedSuppliers({
          page: null,
          limit: null,
          generalSearch: query,
          sortBy: 'supplierName',
          sort: 'asc',
        })
      );
    }, 500),
    [dispatch]
  );

  const scrollToBottom = () => {
    if (tableContainerRef.current) {
      const scrollContainer = tableContainerRef.current.querySelector('.simplebar-content-wrapper');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  };

  const handleAppendItem = () => {
    append(defaultItem);
    setTimeout(scrollToBottom, 100);
  };

  useEffect(() => {
    fetchPaginatedSuppliers(searchQuery);

    return fetchPaginatedSuppliers.cancel;
  }, [searchQuery, fetchPaginatedSuppliers]);

  useEffect(() => {
    let calculatedSubTotal = 0;

    watchItems.forEach((item, index) => {
      const calculatedTotal = (item.unitPrice || 0) * item.quantity - item.discount;

      calculatedSubTotal += calculatedTotal;

      if (calculatedTotal !== item.totalAfterDiscount) {
        setValue(`items.${index}.totalAfterDiscount`, calculatedTotal);
      }
    });

    setSubTotal(calculatedSubTotal);

    setValue('totalAfterDiscount', calculatedSubTotal - watchDiscount);
  }, [watchItems, setValue, watchDiscount]);

  useEffect(() => {
    setValue('totalAfterDiscount', subTotal - watchDiscount);
  }, [setValue, watchDiscount, subTotal, watch]);

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="flex-start">
        <Card sx={{ p: 3, flexGrow: 1, minWidth: 0, width: 1 }}>
          <Grid container spacing={2}>
            <Grid
              mb={1}
              display="flex"
              mt={2}
              alignItems="center"
              justifyContent="space-between"
              width={1}
            >
              <Typography variant="subtitle2">Order Information</Typography>
            </Grid>

            <Grid xs={12} md={6}>
              <Controller
                name="purchaseDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <CustomDatePicker
                    disableFuture
                    label="Purchase Date"
                    value={field.value}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                        required: true,
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid xs={12} md={6}>
              <Controller
                name="supplier"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    fullWidth
                    multiple={false}
                    options={suppliers.suppliers.map((supplier: ISupplierListItem) => supplier)}
                    getOptionLabel={(option: ISupplierListItem | string) => {
                      try {
                        if (typeof option === 'string') {
                          if (option.length === 0) {
                            return option;
                          }

                          const value: ISupplierListItem = suppliers.suppliers.find(
                            (supplierItem: ISupplierListItem) => supplierItem._id === option
                          );

                          return value?.supplierName ?? '';
                        }

                        return option.supplierName;
                      } catch (e) {
                        console.warn(e);
                        return '';
                      }
                    }}
                    renderOption={(props, option: ISupplierListItem | string) => {
                      if (typeof option === 'string') {
                        return (
                          <li {...props} key={option}>
                            {option}
                          </li>
                        );
                      }

                      return (
                        <li {...props} key={option._id}>
                          {option.supplierName}
                        </li>
                      );
                    }}
                    onInputChange={(_, value) => {
                      setSearchQuery(value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        label="Supplier"
                        required
                        error={!!error}
                        helperText={error ? error?.message : ''}
                        {...params}
                      />
                    )}
                    onChange={(event, newValue) => {
                      const newSupplier =
                        typeof newValue === 'string' ? newValue : newValue?._id ?? '';
                      field.onChange(newSupplier);
                    }}
                  />
                )}
              />
            </Grid>

            <Grid xs={12}>
              <RHFTextField name="remarks" label="Remarks" multiline rows={2} />
            </Grid>

            <Divider
              sx={{
                width: 1,
                my: 2,
                borderStyle: 'dashed',
              }}
            />

            <Grid mb={1} xs={12} width="full">
              <Typography variant="subtitle2">Line Items</Typography>
            </Grid>

            <Grid xs={12}>
              <Stack spacing={2}>
                <TableContainer
                  ref={tableContainerRef}
                  sx={{ position: 'relative', overflow: 'hidden', height: '250px' }}
                >
                  <Scrollbar>
                    <Table stickyHeader size="small" sx={{ minWidth: 600 }}>
                      <TableHeadCustom headLabel={LINE_ITEM_HEADERS} />

                      <TableBody>
                        {fields.map((item, index) => (
                          <PackagingPurchaseOrderNewItemRow
                            key={item.id}
                            index={index}
                            remove={remove}
                            isLast={fields.length === 1 && index === 0}
                            onItemSelected={handleItemSelected}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </Scrollbar>
                </TableContainer>
              </Stack>

              <Stack mt={4}>
                <Button variant="soft" color="primary" onClick={handleAppendItem}>
                  Add
                </Button>
              </Stack>
            </Grid>
          </Grid>

          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={2}
            mt={3}
            divider={<Divider orientation="vertical" flexItem sx={{ borderStyle: 'dashed' }} />}
          >
            <Stack direction="row" gap={2}>
              <Typography variant="subtitle2">Sub Total</Typography>
              <Typography variant="subtitle2">{fCurrencyRupees(subTotal * 100)}</Typography>
            </Stack>

            <Stack direction="row" gap={2} alignItems="center">
              <Typography variant="caption">Discount</Typography>
              <RHFTextField
                size="small"
                name="discount"
                type="number"
                placeholder="0.00"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box sx={{ typography: 'subtitle2', color: 'text.disabled' }}>Rs.</Box>
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: { md: 150 } }}
              />
            </Stack>

            <Stack direction="row" gap={2}>
              <Typography variant="subtitle1">Grand Total</Typography>
              <Typography variant="subtitle1">
                {fCurrencyRupees(watch('totalAfterDiscount') * 100)}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" justifyContent="space-between" gap={2} mt={5}>
            <Link component={RouterLink} href={paths.dashboard.packagingPurchaseOrder.root}>
              <Button variant="outlined" color="inherit">
                Cancel
              </Button>
            </Link>

            <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
              {!currentPurchaseOrder ? 'Create Purchase Order' : 'Save Changes'}
            </LoadingButton>
          </Stack>
        </Card>

        <Box sx={{ width: { xs: 1, lg: 320 }, flexShrink: 0 }}>
          <PackagingPurchaseOrderItemsSummary items={summaryItems} />
        </Box>
      </Stack>
    </FormProvider>
  );
}
